<script setup lang="ts">
import { computed } from "vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import {
  ButtonSeverity,
  ButtonSize,
  isNullish,
  TuButton,
  TuIconButton,
  TuTable,
  TuTableHeader,
  TuTableRow,
} from "@tinyurldev/tu-ui-vue";

import DownloadInvoiceDropdown from "@Modules/billing/components/DownloadInvoiceDropdown.vue";
import type { Invoice } from "@Modules/billing/Data/Models/InvoiceModel";
import type { TransactionFeedbackBanner } from "@Modules/billing/ViewModels/BillingViewModel";
import useTranslation from "@Composables/useTranslation";
import Spinner from "@Components/Spinner.vue";

//
// Setup
const { __ } = useTranslation();
const { smallerOrEqual } = useBreakpoints(breakpointsBootstrapV5);

//
// Props
interface Props {
  invoices: Invoice[];
  isReady?: boolean;
  maxRows?: number | null;
  downloadInvoice: (invoice: Invoice, type: string) => void;
  payInvoice: (invoice: Invoice) => void;
  openHostedInvoice: (invoice: Invoice) => void;
  /** Set while a payment is pending, which keeps every pay button disabled */
  payingInvoiceId?: string | number | null;
  /** Set while the provider's page is being asked for, which spins that row's hosted button */
  hostedInvoiceId?: string | number | null;
  feedbackBanner?: TransactionFeedbackBanner | null;
  payAll?: (() => void) | null;
}

const props = withDefaults(defineProps<Props>(), {
  isReady: false,
  maxRows: null,
  payingInvoiceId: null,
  hostedInvoiceId: null,
  feedbackBanner: null,
  payAll: null,
});

//
// Data
const isMobileView = smallerOrEqual("xl");

//
// Computed
const visibleInvoices = computed<Invoice[]>(() => {
  if (props.maxRows === null) {
    return props.invoices;
  }

  return props.invoices.slice(0, props.maxRows);
});

const isEmpty = computed<boolean>(() => props.isReady && visibleInvoices.value.length === 0);

const isPaying = computed<boolean>(() => !isNullish(props.payingInvoiceId));

//
// Methods
/**
 * Passes the banner's call to action on to the page that knows how to settle the balance.
 */
function triggerPayAll(): void {
  props.payAll?.();
}

/**
 * Swaps the hosted button's icon for a spinner while its page is being asked for, since
 * TuIconButton has no loading state of its own.
 */
function hostedInvoiceIconClass(invoiceId: string | number): string {
  return props.hostedInvoiceId === invoiceId ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-file-alt";
}

//
// Exposed Test Vars
defineExpose({ visibleInvoices, isEmpty, isPaying, triggerPayAll, hostedInvoiceIconClass });
</script>

<template>
  <TuTable
    class="transaction-table _transaction_table"
    :class="{
      'transaction-table--mobile': isMobileView,
      'transaction-table--capped': !isNullish(maxRows),
      'transaction-table--with-banner': !isNullish(feedbackBanner),
      _feedback_banner: !isNullish(feedbackBanner),
    }"
    :feedback-banner="feedbackBanner"
    @feedback-banner-cta-click="triggerPayAll"
  >
    <template #feedback-banner-content>
      <slot name="feedback-banner-content" />
    </template>

    <template #header>
      <TuTableHeader>
        <th v-if="isMobileView">
          {{ __("settings_billing_page.transaction.table.transaction") }}
        </th>
        <template v-else>
          <th class="transaction-table__status-cell">
            {{ __("settings_billing_page.transaction.table.status") }}
          </th>
          <th class="transaction-table__description-cell">
            {{ __("settings_billing_page.transaction.table.description") }}
          </th>
          <th class="transaction-table__date-cell">
            {{ __("settings_billing_page.transaction.table.date") }}
          </th>
          <th class="transaction-table__total-cell">
            {{ __("settings_billing_page.transaction.table.total") }}
          </th>
          <th class="transaction-table__action-cell transaction-table__action-cell--align-right">
            {{ __("settings_billing_page.transaction.table.action") }}
          </th>
        </template>
      </TuTableHeader>
    </template>

    <TuTableRow v-if="!isReady">
      <td
        class="transaction-table__spinner-cell _spinner"
        :colspan="isMobileView ? 1 : 5"
      >
        <Spinner size="base" />
      </td>
    </TuTableRow>

    <TuTableRow v-else-if="isEmpty">
      <td
        class="transaction-table__empty-cell _no_records"
        :colspan="isMobileView ? 1 : 5"
      >
        <i class="fa-solid fa-circle-exclamation" />
        {{ __("settings_billing_page.transaction.table.no_records") }}
      </td>
    </TuTableRow>

    <template v-else-if="!isMobileView">
      <TuTableRow
        v-for="invoice in visibleInvoices"
        :key="`${invoice.id}-desktop`"
        class="_transaction_row"
      >
        <td class="transaction-table__status-cell">
          <span
            class="_invoice_status"
            :class="`transaction-table__status transaction-table__status--${invoice.effectiveStatusKey}`"
          >
            {{ invoice.display_status }}
            <i
              v-if="invoice.isUnpaid()"
              class="fa-solid fa-circle-exclamation _unpaid_warning"
            />
          </span>
        </td>
        <td class="transaction-table__description-cell _invoice_description">
          <span v-if="!invoice.hasDescriptionSegments()">—</span>
          <div
            v-else
            class="transaction-table__description-segments"
          >
            <span
              v-for="(segment, index) in invoice.descriptionSegments"
              :key="segment.id"
            >
              <span v-if="index > 0"> | </span>
              <span
                v-if="segment.label"
                class="transaction-table__description-highlighter"
              >{{ segment.label }}</span>
              {{ segment.value }}
            </span>
          </div>
        </td>
        <td class="transaction-table__date-cell _invoice_date">
          {{ invoice.formattedDate }}
        </td>
        <td class="transaction-table__total-cell _invoice_total">
          {{ invoice.formattedTotal }}
        </td>
        <td
          align="right"
          class="transaction-table__action-cell"
        >
          <div class="transaction-table__actions transaction-table__actions--right">
            <TuButton
              v-if="invoice.isPayable()"
              class="_pay_now"
              :class="{ _pay_now_loading: payingInvoiceId === invoice.id }"
              icon-class="fa-solid fa-shield-alt"
              :is-disabled="isPaying"
              :is-loading="payingInvoiceId === invoice.id"
              :label="__('settings_billing_page.transaction.pay_now_button')"
              :severity="ButtonSeverity.SECONDARY"
              :size="ButtonSize.MEDIUM"
              @click="payInvoice(invoice)"
            />

            <TuIconButton
              v-if="invoice.hasHostedInvoiceUrl()"
              class="transaction-table__hosted-button _hosted_invoice"
              :class="{ _hosted_invoice_loading: hostedInvoiceId === invoice.id }"
              :aria-label="__('settings_billing_page.transaction.hosted_invoice_label')"
              is-outlined
              :size="ButtonSize.MEDIUM"
              :is-disabled="hostedInvoiceId === invoice.id"
              :icon-class="hostedInvoiceIconClass(invoice.id)"
              @click="openHostedInvoice(invoice)"
            />

            <DownloadInvoiceDropdown
              v-else
              :invoice="invoice"
              @select="downloadInvoice(invoice, $event)"
            />
          </div>
        </td>
      </TuTableRow>
    </template>

    <template v-else>
      <TuTableRow
        v-for="invoice in visibleInvoices"
        :key="`${invoice.id}-mobile`"
        class="_transaction_row"
      >
        <td>
          <div class="transaction-table__mobile-row">
            <span
              class="_invoice_status"
              :class="`transaction-table__status transaction-table__status--${invoice.effectiveStatusKey}`"
            >
              {{ invoice.display_status }}
              <i
                v-if="invoice.isUnpaid()"
                class="fa-solid fa-circle-exclamation _unpaid_warning"
              />
            </span>
          </div>
          <div class="transaction-table__mobile-row _invoice_description">
            <span v-if="!invoice.hasDescriptionSegments()">—</span>
            <div
              v-else
              class="transaction-table__description-segments"
            >
              <span
                v-for="(segment, index) in invoice.descriptionSegments"
                :key="segment.id"
              >
                <span v-if="index > 0"> | </span>
                <span
                  v-if="segment.label"
                  class="transaction-table__description-highlighter"
                >{{ segment.label }}</span>
                {{ segment.value }}
              </span>
            </div>
          </div>
          <div class="transaction-table__mobile-row">
            <p class="transaction-table__mobile-title">
              {{ __("settings_billing_page.transaction.table.date") }}:
            </p>
            <div class="transaction-table__mobile-content _invoice_date">
              {{ invoice.formattedDate }}
            </div>
          </div>
          <div class="transaction-table__mobile-row">
            <p class="transaction-table__mobile-title">
              {{ __("settings_billing_page.transaction.table.total") }}:
            </p>
            <div class="transaction-table__mobile-content _invoice_total">
              {{ invoice.formattedTotal }}
            </div>
          </div>
          <div class="transaction-table__mobile-row">
            <p class="transaction-table__mobile-title">
              {{ __("settings_billing_page.transaction.table.action") }}:
            </p>
            <div class="transaction-table__mobile-content transaction-table__actions">
              <TuButton
                v-if="invoice.isPayable()"
                class="_pay_now"
                :class="{ _pay_now_loading: payingInvoiceId === invoice.id }"
                icon-class="fa-solid fa-shield-alt"
                :is-disabled="isPaying"
                :is-loading="payingInvoiceId === invoice.id"
                :label="__('settings_billing_page.transaction.pay_now_button')"
                :severity="ButtonSeverity.SECONDARY"
                :size="ButtonSize.MEDIUM"
                @click="payInvoice(invoice)"
              />

              <TuIconButton
                v-if="invoice.hasHostedInvoiceUrl()"
                class="transaction-table__hosted-button _hosted_invoice"
                :class="{ _hosted_invoice_loading: hostedInvoiceId === invoice.id }"
                :aria-label="__('settings_billing_page.transaction.hosted_invoice_label')"
                is-outlined
                :size="ButtonSize.MEDIUM"
                :is-disabled="hostedInvoiceId === invoice.id"
                :icon-class="hostedInvoiceIconClass(invoice.id)"
                @click="openHostedInvoice(invoice)"
              />

              <DownloadInvoiceDropdown
                v-else
                :invoice="invoice"
                @select="downloadInvoice(invoice, $event)"
              />
            </div>
          </div>
        </td>
      </TuTableRow>
    </template>
  </TuTable>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

// acceptable desktop height to render exact 5 rows, may change depending on content
$table-body-desktop-height: 245px;
$table-body-desktop-full-height: 588px;

// acceptable mobile height to render exact 5 rows, may change depending on content
$table-body-mobile-height: 595px;
$table-body-mobile-full-height: 1428px;

.transaction-table {
  $self: &;

  &__status-cell {
    width: 125px;
  }

  &__description-cell {
    width: auto;
  }

  &__date-cell {
    width: 196px;
  }

  &__total-cell {
    width: 115px;
  }

  &__action-cell {
    width: 155px;
    white-space: nowrap;

    &--align-right {
      text-align: right;
    }
  }

  &__status {
    font-weight: typography.$font-weight-bold;

    &--unpaid {
      color: color.$alert;
    }

    &--voided {
      color: color.$disabled;
    }

    &--paid {
      color: color.$secondary;
    }

    &--refunded {
      color: color.$primary;
    }
  }

  &__description-highlighter {
    font-weight: typography.$font-weight-bold;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: spacing.$xxxs;

    &--right {
      justify-content: flex-end;
    }
  }

  &__spinner-cell {
    padding: spacing.$xs;
  }

  &__empty-cell {
    text-align: center;
  }

  &__mobile-row {
    display: flex;

    &:not(:last-child) {
      margin-bottom: spacing.$xxxs;
    }
  }

  &__mobile-title {
    flex: 1;
    font-weight: typography.$font-weight-bold;
    margin-bottom: 0;
  }

  &__mobile-content {
    flex: 1;
  }

  // the attached banner only ever flags an unsettled balance, so the table it sits on is
  // outlined to match it instead of keeping its resting grey.
  &--with-banner {
    border-color: color.$alert;
  }

  &--capped {
    :deep(.tu-table__body) {
      max-height: $table-body-desktop-height;
    }
  }

  &--mobile {
    :deep(.tu-table__body) {
      max-height: $table-body-mobile-full-height;
    }

    &#{$self}--capped {
      :deep(.tu-table__body) {
        max-height: $table-body-mobile-height;
      }
    }
  }

  :deep(.tu-table__body) {
    max-height: $table-body-desktop-full-height;
  }
}
</style>
